import React from 'react';
import {
  Table, TableHead, TableBody, TableCell, TableRow,
  TextField, IconButton, TableContainer, Paper, Typography, Tooltip, InputAdornment
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import { withStyles } from '@material-ui/styles';
import {
  handlePercentageChange,
  handlePercentageKeyDown,
  handlePercentagePaste
} from '../../utils/leanixUtils';

const styles = {
  label: { color: 'gray', fontSize: 15, fontWeight: 'bold', textAlign: 'left', paddingBottom: 0 },
  percentageInput: { '& .MuiInputBase-root': { border: 'none' } },

  tableWrapper: { 
    marginTop: 4, 
    marginBottom: 16, 
    borderRadius: 14, 
    border: '1px solid #ccc', 
    boxShadow: 'none',
    overflow: 'hidden',
        paddingTop: 0, 
    paddingBottom: 0, 
  },
  tableWrapperError: { 
    marginTop: 4, 
    marginBottom: 12, 
    border: '1px solid red', 
    borderRadius: 14, 
    boxShadow: 'none',
    overflow: 'hidden',
    paddingTop: 0, 
    paddingBottom: 0, 
  },
  tableWrapperAdded: { 
    border: '1px solid #4da6ff',
    borderRadius: 14,
    boxShadow: 'none',
    overflow: 'hidden',
    paddingTop: 0, 
    paddingBottom: 0, 
  },
  tableWrapperDeleted: { 
    border: '1px solid red',
    borderRadius: 14,
    boxShadow: 'none',
    overflow: 'hidden',
    paddingTop: 0, 
    paddingBottom: 0, 
  },

  tableContainer: { 
    paddingTop: 0, 
    paddingBottom: 0, 
    overflowX: 'auto',
    '& .MuiTable-root': {
      borderRadius: 'inherit',
    }
  },

  tableContainerError: { marginTop: 4, marginBottom: 12, border: '1px solid red', borderRadius: 4, boxShadow: 'none', overflow: 'hidden' },
  rowAdded: { backgroundColor: '#e1f5fe' },
  rowDeleted: { backgroundColor: '#ff9999' },
  remainingText: { color: 'gray', marginBottom: 12 },
  remainingTextError: { color: 'red' },
  summaryErrorText: { color: 'red' },
  cellChanged: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#e1f5fe',
      '& fieldset': { borderColor: '#39acff' },
      '&:hover fieldset': { borderColor: '#39acff' },
      '&.Mui-disabled fieldset': { borderColor: '#39acff' },
    }
  },
  tableAdded: { border: '1px solid #4da6ff' },
  tableDeleted: { border: '1px solid red' }
};
const LeanIXMappingGrid = ({
  mappings = [],
  onRemove,
  label,
  isReadOnly = false,
  remainingPercentage,
  fieldErrors = [],
  summaryError = "",
  classes,
  onLeanIXMappingChanged,
  changes = [],
  tableChange = null
}) => {

  const onChangePercentage = (baseIndex, val) => {
  if (baseIndex < 0) return; // skip for deleted rows
  handlePercentageChange(val, newVal => {
    const updated = mappings.map((item, i) => 
      i === baseIndex ? { ...item, percentage: newVal } : item
    );
    onLeanIXMappingChanged(updated);
  }, () => {});
};

  const getDiffId = (d) =>
  d.mapping?.leanIXId || d.current?.leanIXId || d.previous?.leanIXId;

let combinedRows;
if (isReadOnly && changes?.length > 0) {

  combinedRows = changes.map(d => {
    const rowData =
      d.type === 'row-deleted'
        ? d.mapping || d.previous || {}
        : d.mapping || d.current || {};
    return { ...rowData, __diff: d };
  });
} else if (tableChange =="table-deleted" && !isReadOnly) {

  combinedRows = [

    ...(changes || [])
      .filter(d => d.type === 'row-deleted')
      .map(d => ({
        ...(d.mapping || d.previous || {}),
        __diff: d,
        __baseIndex: -1,
      })),


    ...mappings.map((m, idx) => {
      const diff = (changes || []).find(d => getDiffId(d) === m.leanIX.id);

      if (diff) {
        return { ...m, __diff: null, __isNewRow: true, __baseIndex: idx };
      }

      return { ...m, __diff: null, __baseIndex: idx };
    }),
  ];
}
else {
  combinedRows = [
    ...mappings.map((m, idx) => {
      const diff = (changes || []).find(d => getDiffId(d) === m.leanIX.id);

      if (diff && diff.type === "row-deleted") {
        return { ...m, __diff: null, __isNewRow: true, __baseIndex: idx };
      }

      return { ...m, __diff: diff || null, __baseIndex: idx };
    }),
    ...(changes || [])
      .filter(d => d.type === 'row-deleted')
      .map(d => ({ ...(d.mapping || d.previous || {}), __diff: d, __baseIndex: -1 }))
  ];
}

if (!(tableChange =="table-deleted" && !isReadOnly)) {
  combinedRows = combinedRows.sort(
    (a, b) =>
      (a.__diff?.sequence ?? a.__baseIndex ?? 9999) -
      (b.__diff?.sequence ?? b.__baseIndex ?? 9999)
  );
}

  return (
    <>
      {label && <h3 className={classes.label}>{label}</h3>}

        {combinedRows.length > 0 && (
                  <div className={`
                    ${classes.tableWrapper} 
                    ${summaryError ? classes.tableWrapperError : ''} 
                    ${isReadOnly && tableChange === 'table-added' ? classes.tableWrapperAdded : ''} 
                    ${isReadOnly && tableChange === 'table-deleted' ? classes.tableWrapperDeleted : ''}
                  `}>
        <TableContainer className={classes.tableContainer}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>LeanIX ID</strong></TableCell>
                <TableCell><strong>LeanIX Name</strong></TableCell>
                <TableCell><strong>Percentage</strong></TableCell>
                {!isReadOnly && <TableCell />}
              </TableRow>
            </TableHead>

            <TableBody>
              {combinedRows.map((m, index) => {
                const diff = m.__diff;
                const id = diff?.type === 'row-deleted'
                ? `deleted-${m.leanIXId || m.leanIX.id || index}`
                : diff?.type === 'row-added'
                  ? `added-${m.leanIXId || m.leanIX.id || index}`
                  : m.leanIXId || m.leanIX.id || `row-${index}`;


                let rowClass = '';
                if (diff?.type === 'row-added') rowClass = classes.rowAdded;
                if (diff?.type === 'row-deleted') rowClass = classes.rowDeleted;

                // Percentage cell
                let percentageCell;
                const isChangedPercentage = diff?.type === 'row-changed' && diff.changes?.percentage;

                  if (diff?.type === 'row-deleted') {
                      const mPrev = diff.mapping || diff.previous || {};
                      return (
                        <TableRow key={id} className={classes.rowDeleted}>
                          <TableCell>{mPrev.leanIX?.label || mPrev.leanIxUniqueId || ''}</TableCell>
                          <TableCell>{mPrev.leanIX?.value || mPrev.leanIxName || ''}</TableCell>
                          <TableCell><Typography>{mPrev.percentage}%</Typography></TableCell>
                          {!isReadOnly && <TableCell />}
                        </TableRow>
                      );
                    
                } else if (isChangedPercentage) {
                    percentageCell = (
                      <Tooltip title={`${diff.changes.percentage.old}%`} arrow placement="top">
                        <TextField
                          type="text"
                          value={m.percentage ?? ""}
                          onChange={(e) => onChangePercentage(m.__baseIndex, e.target.value)}
                          onKeyDown={handlePercentageKeyDown}
                          onPaste={handlePercentagePaste}
                          size="small"
                          variant="outlined"
                          className={`${classes.percentageInput} ${classes.cellChanged}`}
                          inputProps={{ min: 0, max: 100, step: 0.1, style: { width: 60 } }}
                          disabled={isReadOnly}
                          InputProps={!isReadOnly ? {} : {
                            endAdornment: (
                              <InputAdornment position="end" style={{ margin: 0, padding: 0 }}>%</InputAdornment>
                            )
                          }}
                        />
                      </Tooltip>
                    );
                } else if (isReadOnly) {
                  percentageCell = <Typography>{m.percentage}%</Typography>;
                } else {
                  percentageCell = (
                    <TextField
                      type="text"
                      value={m.percentage ?? ""}
                      onChange={(e) => onChangePercentage(m.__baseIndex, e.target.value)}
                      onKeyDown={handlePercentageKeyDown}
                      onPaste={handlePercentagePaste}
                      error={!!fieldErrors?.[m.__baseIndex]?.percentage}
                      helperText={fieldErrors?.[m.__baseIndex]?.percentage || ""}
                      size="small"
                      variant="outlined"
                      className={classes.percentageInput}
                      inputProps={{ min: 0, max: 100, step: 0.1, style: { width: 60 } }}
                    />
                  );
                }


                return (
                  <TableRow key={id} className={rowClass}>
                    <TableCell>{m.leanIX?.label || m.leanIxUniqueId || ''}</TableCell>
                    <TableCell>{m.leanIX?.value || m.leanIxName || ''}</TableCell>
                    <TableCell>{percentageCell}</TableCell>
                    {!isReadOnly && diff?.type !== 'row-deleted' && (
                      <TableCell>
                        <IconButton onClick={() => onRemove(m.__baseIndex)} aria-label="delete">
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    )}
                    {!isReadOnly && diff?.type === 'row-deleted' && <TableCell />}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
         </div>
      )}

      {summaryError && (
        <Typography variant="body2" className={classes.summaryErrorText}>{summaryError}</Typography>
      )}

      {!isReadOnly && (
        <Typography
          variant="body2"
          className={remainingPercentage < 0 ? classes.remainingTextError : classes.remainingText}
        >
          Remaining percentage to allocate: {remainingPercentage}%
        </Typography>
      )}
    </>
  );
};

export default withStyles(styles)(LeanIXMappingGrid);